<template>
  <span class="animated-counter" :class="pulseClass">{{ displayText }}</span>
</template>

<script>
/**
 * AnimatedCounter — a self-contained (no external lib) integer that tweens to a
 * new target on change over ~500ms ease-out, driven by requestAnimationFrame.
 * Snaps instantly when the OS prefers reduced motion, renders with tabular-nums
 * so digits don't jitter, and briefly pulses green when the number rises / amber
 * when it falls. Tween math lives in utils/creditCounter (pure, unit-tested).
 *
 * Usage: <AnimatedCounter :value="creditsAvailable" />
 */
import { tweenValue, prefersReducedMotion, DEFAULT_TWEEN_MS } from '../utils/creditCounter';

export default {
  name: 'AnimatedCounter',
  props: {
    value: { type: Number, default: 0 },
    duration: { type: Number, default: DEFAULT_TWEEN_MS },
    // Emit a short colour pulse on change (green up / amber down).
    pulse: { type: Boolean, default: true },
  },
  data() {
    return {
      displayValue: Math.round(Number(this.value) || 0),
      pulseClass: null,
      rafId: null,
      pulseTimer: null,
    };
  },
  computed: {
    displayText() {
      return Math.round(this.displayValue).toLocaleString();
    },
  },
  watch: {
    value(next, prev) {
      this.animateTo(Number(next) || 0, Number(prev) || 0);
    },
  },
  methods: {
    animateTo(target, previous) {
      this.cancel();
      if (this.pulse && target !== previous) {
        this.flashPulse(target > previous ? 'is-rising' : 'is-falling');
      }
      const from = this.displayValue;
      if (prefersReducedMotion() || this.duration <= 0) {
        this.displayValue = target; // snap
        return;
      }
      const now = () => (typeof performance !== 'undefined' && performance.now)
        ? performance.now() : Date.now();
      const start = now();
      const step = () => {
        const elapsed = now() - start;
        this.displayValue = tweenValue(from, target, elapsed, this.duration);
        if (elapsed < this.duration) {
          this.rafId = requestAnimationFrame(step);
        } else {
          this.displayValue = target; // land exactly on target
          this.rafId = null;
        }
      };
      this.rafId = requestAnimationFrame(step);
    },
    flashPulse(cls) {
      this.pulseClass = cls;
      if (this.pulseTimer) clearTimeout(this.pulseTimer);
      this.pulseTimer = setTimeout(() => { this.pulseClass = null; }, 600);
    },
    cancel() {
      if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
    },
  },
  beforeDestroy() {
    this.cancel();
    if (this.pulseTimer) clearTimeout(this.pulseTimer);
  },
};
</script>

<style scoped>
.animated-counter {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  transition: color 0.3s ease;
}
.animated-counter.is-rising { color: #2e9e5b; }
.animated-counter.is-falling { color: #a8620d; }
</style>
